/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useId } from '@coursera/cds-core';

import { ToggleTabStateContext } from 'bundles/cds-labs/components/ToggleTab/hooks/useToggleTabContext';
import type { ToggleTabValue } from 'bundles/cds-labs/components/ToggleTab/types';

export interface ToggleTabContextProps {
  value: ToggleTabValue;
  invert: boolean;
  children: React.ReactNode;
  onChange: (value: ToggleTabValue) => void;
  id?: string;
}

const styles = {
  container: css`
    width: 100%;
  `,
};

const ToggleTabContext = ({ children, value, invert, onChange, id: providedId }: ToggleTabContextProps) => {
  const generatedId = useId();
  const id = providedId || `toggle-tab-${generatedId}`;
  const tabRefs = React.useRef<Map<ToggleTabValue, React.RefObject<HTMLButtonElement>>>(new Map());

  // Manage indicator state in context
  const [indicatorStyle, setIndicatorStyle] = React.useState({ width: 0, left: 0 });

  const registerTabRef = React.useCallback<(tabValue: ToggleTabValue, ref: React.RefObject<HTMLButtonElement>) => void>(
    (tabValue: ToggleTabValue, ref: React.RefObject<HTMLButtonElement>) => {
      tabRefs.current.set(tabValue, ref);
    },
    []
  );

  const updateIndicatorPosition = React.useCallback<() => void>(() => {
    const activeTabRef = tabRefs.current.get(value);
    if (activeTabRef?.current) {
      const { offsetWidth, offsetLeft } = activeTabRef.current;
      setIndicatorStyle({ width: offsetWidth, left: offsetLeft });
    }
  }, [value]);

  return (
    <ToggleTabStateContext.Provider
      value={{
        value,
        id,
        invert,
        onChange,
        registerTabRef,
        updateIndicatorPosition,
        indicatorStyle,
      }}
    >
      <div css={styles.container}>{children}</div>
    </ToggleTabStateContext.Provider>
  );
};

export default ToggleTabContext;
